
import * as React from 'react';
import { connectAdvanced } from 'react-redux';
import { IItemCache, IItemCacheSelector } from '../../models/IItemCacheObserver';
import { IComponentDecorator, IDecoratedComponentType, IDecoratedProps, withDisplayName } from '../../utilities/components/Decorators';
import { IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { IItemCacheState } from '../../store/reducers/itemCacheReducer';
import { ItemCacheObserver, IItemCacheDependencies, IItemCacheDemands } from '../../models/itemCache/ItemCacheObserver';
import { areShallowDifferent, assign } from '@ms/utilities-objects/lib/index';
import { withItemCacheDemander, IItemCacheDemanderProps } from './withItemCacheDemander';
import { ITEM_CACHE_STORE_KEY } from '../itemCacheProvider/ItemCacheProvider';
import { IControlFaceted, CONTROL_HANDLER_CONTEXT } from './withControlHandler';
import { Facet, IFaceted } from '../../models/Facet';
import { excludeResolvedDemands } from '../../operations/getItems';
import { itemCacheSuspenseControl } from '../../controls/itemCacheSuspenseControl';

/**
 * Facet which marks a component as having been produced using `connectToItemCache`.
 * The original mapper function and underlying component may be extracted and evaluated
 * directly if necessary, to optimize performance.
 * Care should be taken to pass the underlying component's props to the mapper.
 * @internal
 */
export const connectToItemCacheLinkFacet = new Facet<{
  mapItemCacheToProps: IItemCacheSelector<{}, {}>;
  component: React.ComponentType<{}>;
}>('connectToItemCacheLink');

/**
 * Props interface for a component which optionally consumes the dispatcher for the item cache store.
 * @public
 */
export interface IItemCacheDispatchProp {
  itemCacheDispatch: IItemCacheDispatch;
}

interface IConnectedControlHandlerProps {
  itemCacheConnectorControlHandlers: IControlFaceted;
}

/**
 * Interface for the decorator produced by connectToItemCache, which accounts for the dispatch prop mapping.
 * @public
 */
export interface IConnectToItemCacheDecorator<TInjectedProps, TOwnProps> extends IComponentDecorator<TInjectedProps, TOwnProps> {
  <P extends (TInjectedProps & IItemCacheDispatchProp)>(
    component: React.ComponentType<P>
  ): IDecoratedComponentType<P, TInjectedProps & IItemCacheDispatchProp, TOwnProps>;
}

/**
 * Creates a higher-order component which has some props injected based on mappings
 * from the current state of the item cache.
 * @param mapItemCacheToProps - A function which maps the current state of the item cache
 * to props for the component.
 * @public
 */
export function connectToItemCache<TInjectedProps, TOwnProps = {}>(
  mapItemCacheToProps?: IItemCacheSelector<TInjectedProps, TOwnProps>
): IConnectToItemCacheDecorator<TInjectedProps, TOwnProps>;
/**
 * Creates a higher-order component which has some props injected based on mappings
 * from the current state of the item cache.
 * @param mapItemCacheToProps - A function which maps the current state of the item cache
 * to props for the component.
 * @param mapItemCacheDispatchToProps - A function which maps the current store dispatcher for the item cache
 * to props for the component.
 * @public
 */
export function connectToItemCache<TInjectedProps, TDispatchProps = {}, TOwnProps = {}>(
  mapItemCacheToProps: IItemCacheSelector<TInjectedProps, TOwnProps> | undefined,
  mapItemCacheDispatchToProps: (dispatch: IItemCacheDispatch, props: TOwnProps) => TDispatchProps,
): IComponentDecorator<TInjectedProps & TDispatchProps, TOwnProps>;
/**
 * Implementation of `connectToItemCache`.
 * @param mapItemCacheToProps - A function which maps the current state of the item cache
 * to props for the component.
 * @param mapItemCacheDispatchToProps - A function which maps the current store dispatcher for the item cache
 * to props for the component.
 * @internal
 */
export function connectToItemCache<TInjectedProps, TOwnProps = {}>(
  mapItemCacheToProps?: IItemCacheSelector<TInjectedProps, TOwnProps>,
  mapItemCacheDispatchToProps?: (dispatch: IItemCacheDispatch, props: TOwnProps) => TInjectedProps,
): IComponentDecorator<TInjectedProps, TOwnProps> {
  return <P extends TInjectedProps>(
    component: React.ComponentType<P>
  ): IDecoratedComponentType<P, TInjectedProps, TOwnProps> => {
    // A 'back-reference' to the final output component, which can be passed to the `ItemCacheObserver`
    // instances so conditional breakpoints may be used to diagnose the selectors for specific components.
    let debugComponent: IDecoratedComponentType<P, TInjectedProps, TOwnProps>;

    const getOwner = () => debugComponent;

    if (!mapItemCacheToProps) {
      const mapAdvanced =
        // The final result must be the same object if nothing ultimately changed.
        // Redux relies on this memoization to avoid re-renders.
        // dbounceResult MUST be the outer-most decorator applied here.
        debounceResult(
          withBaseProps(
            debounceProps(
              createMapItemCacheDispatchToPropsMapper(mapItemCacheDispatchToProps)
            )
          )(component)
        );

      return withDisplayName('ConnectToItemCache')(connectAdvanced(mapAdvanced, {
        storeKey: ITEM_CACHE_STORE_KEY
      })(component));
    } else {
      const connectToItemCacheLink = connectToItemCacheLinkFacet.unpack(component as {} as IFaceted);

      const finalMapItemCacheToProps = connectToItemCacheLink && !mapItemCacheDispatchToProps ?
        (itemCache: IItemCache, props: TOwnProps) => {
          const { dispatch } = itemCache;

          const outerResult = mapItemCacheToProps(itemCache, props);

          const innerResult = connectToItemCacheLink.mapItemCacheToProps(itemCache, {
            ...(props as {}),
            // Need to include the default dispatch mapping when evaluating the wrapped
            // mapper.
            itemCacheDispatch: dispatch,
            ...(outerResult as {})
          }) as {} as typeof outerResult;

          return {
            // Need to include the outer component's injected props, since they are expected
            // to be available to the wrapped component even if ignored by the inner
            // decorator.
            ...(outerResult as {}),
            ...(innerResult as {})
          } as typeof outerResult;
        } :
        mapItemCacheToProps;

      const finalComponent = connectToItemCacheLink && !mapItemCacheDispatchToProps ?
        connectToItemCacheLink.component as {} as React.ComponentType<P> :
        component;

      const WithItemCacheDemander = withItemCacheDemander()(finalComponent);

      const mapAdvanced =
        // The final result must be the same object if nothing ultimately changed.
        // Redux relies on this memoization to avoid re-renders.
        // dbounceResult MUST be the outer-most decorator applied here.
        debounceResult(
          withBaseProps(
            debounceProps(
              mergeMappers(
                createMapItemCacheToPropsMapper(finalMapItemCacheToProps, getOwner),
                createMapItemCacheDispatchToPropsMapper(mapItemCacheDispatchToProps)
              )
            )
          )(WithItemCacheDemander)
        );

      const Connected = connectAdvanced(mapAdvanced, {
        storeKey: ITEM_CACHE_STORE_KEY
      })(WithItemCacheDemander) as {} as React.ComponentType<
      IDecoratedProps<P, TInjectedProps, TOwnProps & IConnectedControlHandlerProps>
      >;

      class WithControlHandlers extends React.PureComponent<
        IDecoratedProps<P, TInjectedProps, TOwnProps>
        > {
        public render(): JSX.Element {
          const {
            props
          } = this;

          return (
            <CONTROL_HANDLER_CONTEXT.Consumer>
              { (controlHandlers: IControlFaceted) => {
                return (
                  <Connected
                    { ...{
                      ...(props as {}),
                      itemCacheConnectorControlHandlers: controlHandlers
                    } as IDecoratedProps<P, TInjectedProps, TOwnProps & IConnectedControlHandlerProps> }
                  />
                );
              } }
            </CONTROL_HANDLER_CONTEXT.Consumer>
          );
        }
      }

      if (!mapItemCacheDispatchToProps) {
        assign(WithControlHandlers, connectToItemCacheLinkFacet.pack({
          mapItemCacheToProps: finalMapItemCacheToProps as {} as IItemCacheSelector<{}, {}>,
          component: finalComponent as {} as React.ComponentType<{}>
        }));
      }

      debugComponent = WithControlHandlers;

      return withDisplayName('ConnectToItemCache')(
        WithControlHandlers
      );
    }
  };
}

interface IMemoizedMapper<TInjectedProps, TOwnProps> {
  (dispatch: IItemCacheDispatch): (state: IItemCacheState, props: TOwnProps) => TInjectedProps;
}

/**
 * Decorator which ensures a mapper always includes the original props under the result.
 */
function withBaseProps<TInjectedProps, TOwnProps>(
  mapper: IMemoizedMapper<TInjectedProps, TOwnProps>
): <P extends TInjectedProps>(
    component: React.ComponentType<P>
  ) => IMemoizedMapper<P & TInjectedProps, IDecoratedProps<P, TInjectedProps, TOwnProps>> {
  return <P extends TInjectedProps>(
    component: React.ComponentType<P>
  ): IMemoizedMapper<P & TInjectedProps, IDecoratedProps<P, TInjectedProps, TOwnProps>> => {
    return (dispatch: IItemCacheDispatch) => {
      const callback = mapper(dispatch);

      return (state: IItemCacheState, props: IDecoratedProps<P, TInjectedProps, TOwnProps>): P & TInjectedProps => {
        return {
          ...(props as {}),
          ...(callback(state, props) as {})
        } as {} as P & TInjectedProps;
      };
    };
  };
}

/**
 * Decorator which debounces the props passed to a memoized state mapper.
 * A new props object is passed to the mapper only if it differs from the previous props.
 */
function debounceProps<TInjectedProps, TOwnProps>(
  memoizedMapper: IMemoizedMapper<TInjectedProps, TOwnProps>
): IMemoizedMapper<TInjectedProps, TOwnProps> {
  return (dispatch: IItemCacheDispatch) => {
    let previousProps: TOwnProps;

    const callback = memoizedMapper(dispatch);

    return (state: IItemCacheState, props: TOwnProps): TInjectedProps => {
      const havePropsChanged = !previousProps || areShallowDifferent(props, previousProps);

      previousProps = props;

      const finalProps = havePropsChanged ? props : previousProps;

      return callback(state, finalProps);
    };
  };
}

/**
 * Decorator which debounces the result output from a mapper.
 * A new result object is produced only if it differs from the previous exection of the mapper.
 */
function debounceResult<TInjectedProps, TOwnProps>(
  memoizedMapper: IMemoizedMapper<TInjectedProps, TOwnProps>
): IMemoizedMapper<TInjectedProps, TOwnProps> {
  return (dispatch: IItemCacheDispatch) => {
    let previousResult: TInjectedProps;

    const callback = memoizedMapper(dispatch);

    return (state: IItemCacheState, props: TOwnProps): TInjectedProps => {
      const result = callback(state, props);

      const hasResultChanged = !previousResult || areShallowDifferent(result, previousResult);

      return hasResultChanged ? (previousResult = result) : previousResult;
    };
  };
}

/**
 * Merges two state mappers together, producing a new mapper which takes the props from
 * both and merges the output from both.
 * The second mapper's output overwrites matching props from the first.
 */
function mergeMappers<TInjectedProps1, TInjectedProps2, TOwnProps1, TOwnProps2>(
  firstMapper: IMemoizedMapper<TInjectedProps1, TOwnProps1>,
  secondMapper: IMemoizedMapper<TInjectedProps2, TOwnProps2>
): IMemoizedMapper<TInjectedProps1 & TInjectedProps2, TOwnProps1 & TOwnProps2> {
  return (dispatch: IItemCacheDispatch) => {
    const firstCallback = firstMapper(dispatch);
    const secondCallback = secondMapper(dispatch);

    return (
      state: IItemCacheState,
      props: TOwnProps1 & TOwnProps2
    ): TInjectedProps1 & TInjectedProps2 => {
      return {
        ...(firstCallback(state, props) as {}),
        ...(secondCallback(state, props) as {})
      } as TInjectedProps1 & TInjectedProps2;
    };
  };
}

/**
 * Creates a memoized mapper using a given mapItemCacheDispatchToProps function.
 */
function createMapItemCacheDispatchToPropsMapper<TInjectedProps = IItemCacheDispatchProp, TOwnProps = {}>(
  mapItemCacheDispatchToProps?: (dispatch: IItemCacheDispatch, props: TOwnProps) => TInjectedProps
): IMemoizedMapper<TInjectedProps, TOwnProps> {
  return (dispatch: IItemCacheDispatch) => {
    let previousProps: TOwnProps | undefined;

    let dispatchResult: TInjectedProps;

    const defaultDispatchResult: {} = {
      itemCacheDispatch: dispatch
    };

    return (state: IItemCacheState, props: TOwnProps): TInjectedProps => {
      const havePropsChanged = !previousProps || areShallowDifferent(previousProps, props);

      previousProps = props;

      if (havePropsChanged) {
        dispatchResult = mapItemCacheDispatchToProps ?
          mapItemCacheDispatchToProps(dispatch, props) :
          defaultDispatchResult as TInjectedProps;
      }

      return dispatchResult;
    };
  };
}

/**
 * Creates a memoized mapper using a given mapItemCacheToProps function.
 */
function createMapItemCacheToPropsMapper<TInjectedProps, TOwnProps = {}>(
  mapItemCacheToProps: IItemCacheSelector<TInjectedProps, TOwnProps>,
  getOwner: () => ({} | undefined)
): IMemoizedMapper<TInjectedProps & IItemCacheDemanderProps, TOwnProps & IConnectedControlHandlerProps> {
  return (dispatch: IItemCacheDispatch) => {
    let previousDependencies: IItemCacheDependencies | undefined;
    let previousDemands: IItemCacheDemands | undefined;
    let itemCacheResult: TInjectedProps;

    // Determine the component type which initialized this memoized selector.
    const owner = getOwner();

    const defaultItemCacheResult: {} = {};

    let previousProps: (TOwnProps & IConnectedControlHandlerProps) | undefined;
    let previousSelector: ((itemCache: IItemCache) => TInjectedProps) =
      (itemCache: IItemCache) => (defaultItemCacheResult as TInjectedProps);

    return (state: IItemCacheState, props: TOwnProps & IConnectedControlHandlerProps): TInjectedProps & IItemCacheDemanderProps => {
      const {
        // Separate the control handlers from the props,
        // so that they can be compared separately.
        itemCacheConnectorControlHandlers: controlHandlers,
        ...finalProps
      } = props as IConnectedControlHandlerProps;

      const itemCache = new ItemCacheObserver({
        state,
        dispatch,
        previousDependencies,
        previousDemands,
        controlHandlers,
        owner
      });

      try {
        const suspenseControlHandler = itemCache.demandItemFacet(itemCacheSuspenseControl, undefined);

        const {
          mode
        } = itemCacheSuspenseControl.evaluate(suspenseControlHandler)(itemCache, {});

        const isSuspending =
          mode === 'debounce' &&
          !!previousDemands &&
          Object.keys(dispatch(excludeResolvedDemands({ items: previousDemands })).items).length > 0;

        if (!isSuspending) {
          const {
            itemCacheConnectorControlHandlers: previousControlHandlers,
            ...previousFinalProps
          } = previousProps as IConnectedControlHandlerProps || {
            itemCacheConnectorControlHandlers: undefined
          };

          // Compare the props without the control handlers.
          // Note that `previousFinalProps` is never `undefined`, so `previousProps` is used as a proxy here.
          const havePropsChanged = !previousProps || areShallowDifferent(previousFinalProps, finalProps, { debug: true });

          const haveControlHandlersChanged = areShallowDifferent(previousControlHandlers, controlHandlers, { debug: true });

          const selector = havePropsChanged && mapItemCacheToProps ?
            (currentItemCache: IItemCache) => mapItemCacheToProps(currentItemCache, finalProps as TOwnProps) :
            previousSelector;

          if (havePropsChanged || haveControlHandlersChanged || itemCache.haveDependenciesChanged()) {
            // Try to invoke the selector as infrequently as possible.
            // Only invoke if the props have changed, the control handlers have changed, or the cache dependencies
            // have changed.

            // IF YOU WANT TO DIAGNOSE WHY A COMPONENT RE-RENDERED, STICK A BREAKPOINT IN THIS FUNCTION
            // AND SET A CONDITION FOR
            // owner.displayName.indexOf('YourComponent') > -1
            // Example:
            // owner.displayName.indexOf('ItemSetDetailsList') > -1

            ({
              result: itemCacheResult
            } = itemCache.evaluate(selector));

            previousDependencies = itemCache.getDependencies();
            previousDemands = itemCache.getDemands();
          }

          previousProps = props;
          previousSelector = selector;
        }
      } finally {
        // This shuts off the ability to consume from the observer beyond the callback,
        // which is important to guard against out-of-band usage, which could cause problems
        // with state tracking.
        itemCache.dispose();
      }

      return {
        ...(itemCacheResult as {}),
        itemCacheDemanderDemands: previousDemands,
        itemCacheDemanderDependencies: previousDependencies,
        itemCacheDemanderDispatch: dispatch
      } as TInjectedProps & IItemCacheDemanderProps;
    };
  };
}
